import { useEffect, useState } from "react";
import type { ChatMessage } from "../types";
import type { WorkflowStepPayload } from "./contract";
import { isWorkflowExpired, workflowComposerState, type WorkflowComposerState } from "./step";

/** Longest single timer the browser supports (about 24.8 days). */
const MAX_TIMER_MS = 2_147_483_647;

/** A clock that ticks once when `deadline` (ISO date) passes, so a card can close without a new event. */
function useClockAt(deadline: string | undefined): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const at = deadline ? Date.parse(deadline) : Number.NaN;
    if (!Number.isFinite(at)) return undefined;
    const timer = setTimeout(() => {
      setNow(Date.now());
    }, Math.min(Math.max(at - Date.now(), 0), MAX_TIMER_MS));
    return () => {
      clearTimeout(timer);
    };
  }, [deadline]);
  return now;
}

/** True once the card's `expires_at` has passed (re-renders at that moment). */
export function useWorkflowExpired(card: Pick<WorkflowStepPayload, "expires_at">): boolean {
  const now = useClockAt(card.expires_at);
  return isWorkflowExpired(card, now);
}

/** Whole seconds from `now` until `deadline` (ISO date), rounded up; 0 once it passed or when there is none. */
export function secondsUntil(deadline: string | undefined | null, now: number): number {
  const at = deadline ? Date.parse(deadline) : Number.NaN;
  return Number.isFinite(at) ? Math.max(0, Math.ceil((at - now) / 1000)) : 0;
}

/**
 * Seconds left until `deadline`, counting down once a second; the timer only runs while there is time left,
 * so a card whose resend is open (or that has no deadline) costs nothing.
 */
export function useCountdownTo(deadline: string | undefined | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (secondsUntil(deadline, Date.now()) === 0) return undefined;
    const tick = (): void => {
      const current = Date.now();
      setNow(current);
      if (secondsUntil(deadline, current) === 0) clearInterval(timer);
    };
    const timer = setInterval(tick, 1000);
    const first = setTimeout(tick, 0);
    return () => {
      clearInterval(timer);
      clearTimeout(first);
    };
  }, [deadline]);
  return secondsUntil(deadline, now);
}

export interface WorkflowLock {
  /** The card waiting for an answer, if any. */
  card: WorkflowStepPayload | undefined;
  /** The run (or an open card that does not allow typing) locks the composer: it is disabled with a hint. */
  locked: boolean;
}

/** What the run and the last message's workflow card ask of the composer (`open`, `locked` or `reply`); re-evaluated when the card expires. */
export function useWorkflowComposer(messages: readonly ChatMessage[]): WorkflowComposerState {
  const last = messages.at(-1);
  const deadline = last?.action?.type === "workflow_step" ? last.action.payload.expires_at : undefined;
  const now = useClockAt(deadline);
  return workflowComposerState(messages, now);
}

/** `useWorkflowComposer` reduced to "is typing off?" (the console playground and preview use it). */
export function useWorkflowLock(messages: readonly ChatMessage[]): WorkflowLock {
  const { mode, card } = useWorkflowComposer(messages);
  return { card, locked: mode === "locked" };
}
